// Detour's crowd stepped at a fixed dt inside a deterministic sim (decision 0049; the thin spot "Recast inside a
// fixed step", engine/docs/spikes/002-green-valley/notes/b-enemies.md "what we took").
//
// The problem: Detour's crowd keeps state the API can't read or write (each agent's path corridor, its local wall
// boundary, its neighbours, the async path queue), so a save can't hold it and a restored crowd would drift from the
// one that saved. The answer here: the crowd keeps nothing between steps that this wrapper doesn't own.
//   - Every step each agent is re-seated: teleported to the position we hold (which resets Detour's corridor,
//     boundary, neighbours and timers), its velocity written back, then given a velocity request. Detour's own path
//     planning (`requestMoveTarget`, the path queue) is never used.
//   - Paths come from stateless queries (`NavWorld.path`), replanned on a timer the wrapper holds; the agent steers
//     toward its next corner, and the crowd does what it is best at: avoidance between agents, separation, and keeping
//     each move on the mesh.
//   - After `crowd.update(dt)` the wrapper reads every agent's position and velocity back into its own state.
// So the whole crowd is plain data (`snapshot`/`restore`, JSON-safe): a restored crowd is rebuilt with every agent in
// the same Detour slot (the neighbour order follows slots) and continues bit for bit. Same inputs, same crowd.
import { Crowd, type CrowdAgent } from "recast-navigation";
import type { NavVec3 } from "./geometry";
import type { NavWorld } from "./navmesh";

/** An agent's body and limits (m, m/s, m/s²). */
export interface CrowdAgentSpec {
  radius: number;
  height: number;
  maxSpeed: number;
  maxAccel: number;
  /** How hard it keeps apart from neighbours (0 = off; Detour's separation weight). */
  separation?: number;
  /** Steer around other agents (Detour's obstacle avoidance). Default true. */
  avoid?: boolean;
  /** How far it looks for neighbours and walls, and keeps apart by separation (m). Default 6 × radius + 1. */
  queryRange?: number;
}

/** What an agent is trying to do. */
export type AgentGoal =
  | { kind: "none" }
  /** Walk to a point, stopping within `stop` metres of it. */
  | { kind: "point"; at: NavVec3; stop: number; speed?: number }
  /** Move with this velocity (strafe, circle, flee), avoiding the others and staying on the mesh. */
  | { kind: "velocity"; v: NavVec3 };

/** One agent's whole state: plain data. */
export interface CrowdAgentState {
  id: number;
  /** Detour's agent slot (neighbour order follows it, so a restore puts every agent back in its slot). */
  slot: number;
  /** Placed by the game each step (the player): the others avoid it; the crowd doesn't move it. */
  external: boolean;
  spec: CrowdAgentSpec;
  pos: NavVec3;
  vel: NavVec3;
  goal: AgentGoal;
  /** The straight path being followed (corners), the next corner's index, and the goal it was planned for. */
  path: NavVec3[];
  pathAt: number;
  planned: NavVec3 | null;
  /** Seconds until the next replan. */
  replanIn: number;
  /** Within `stop` of a point goal. */
  arrived: boolean;
}

/** A crowd's saved state. */
export interface CrowdSnapshot {
  format: "rpg.nav.crowd@1";
  agents: CrowdAgentState[];
  steps: number;
}

/** The crowd's options (the game's numbers). */
export interface CrowdOptions {
  maxAgents: number;
  maxAgentRadius: number;
  /** Seconds between path replans per agent (staggered by id). */
  replanEvery: number;
  /** A corner counts as reached within this many metres (default 0.35). */
  cornerRadius?: number;
}

/** Planted defects, beside what they break. */
export interface CrowdFaults {
  /** Skip the per-step re-seat: Detour's hidden state (corridors, boundaries) then carries across steps, so a
   *  restored crowd drifts from the one that saved (the determinism test must go red). */
  noReseat: boolean;
}

const len2 = (x: number, z: number): number => Math.hypot(x, z);

/** A Detour crowd as plain, deterministic data. */
export class NavCrowd {
  readonly faults: CrowdFaults = { noReseat: false };
  private crowd: Crowd;
  private live = new Map<number, CrowdAgent>();
  private states = new Map<number, CrowdAgentState>();
  private steps = 0;
  private replans = 0;

  constructor(
    readonly world: NavWorld,
    readonly options: CrowdOptions,
  ) {
    this.crowd = this.fresh();
  }

  private fresh(): Crowd {
    const c = new Crowd(this.world.navMesh, { maxAgents: this.options.maxAgents, maxAgentRadius: this.options.maxAgentRadius });
    c.navMeshQuery.defaultQueryHalfExtents = { x: 2, y: 4, z: 2 };
    return c;
  }

  private params(spec: CrowdAgentSpec) {
    const avoid = spec.avoid ?? true;
    const sep = spec.separation ?? 0;
    // anticipate turns (1) | obstacle avoidance (2) | separation (4)
    const flags = 1 | (avoid ? 2 : 0) | (sep > 0 ? 4 : 0);
    return {
      radius: spec.radius,
      height: spec.height,
      maxSpeed: spec.maxSpeed,
      maxAcceleration: spec.maxAccel,
      separationWeight: sep,
      collisionQueryRange: spec.queryRange ?? spec.radius * 6 + 1,
      pathOptimizationRange: spec.radius * 30,
      updateFlags: flags,
    };
  }

  /** Add an agent at `at` (snapped to the mesh at the next step). Ids are the game's; each id once. */
  add(id: number, at: NavVec3, spec: CrowdAgentSpec, o: { external?: boolean } = {}): CrowdAgentState {
    if (this.states.has(id)) throw new Error(`nav crowd: agent ${id} already added`);
    const agent = this.crowd.addAgent({ x: at[0], y: at[1], z: at[2] }, this.params(spec));
    if (agent.agentIndex < 0) throw new Error(`nav crowd: full (${this.options.maxAgents} agents)`);
    this.live.set(id, agent);
    const s: CrowdAgentState = {
      id,
      slot: agent.agentIndex,
      external: o.external ?? false,
      spec: { ...spec },
      pos: [...at],
      vel: [0, 0, 0],
      goal: { kind: "none" },
      path: [],
      pathAt: 0,
      planned: null,
      replanIn: 0,
      arrived: false,
    };
    this.states.set(id, s);
    return s;
  }

  remove(id: number): void {
    const a = this.live.get(id);
    if (!a) return;
    this.crowd.removeAgent(a);
    this.live.delete(id);
    this.states.delete(id);
  }

  has(id: number): boolean {
    return this.states.has(id);
  }

  private must(id: number): CrowdAgentState {
    const s = this.states.get(id);
    if (!s) throw new Error(`nav crowd: no agent ${id}`);
    return s;
  }

  /** Walk to `at` (at `speed`, default the agent's max), stopping within `stop` m. A goal that moves under 0.5 m keeps
   *  its path until the next replan. */
  moveTo(id: number, at: NavVec3, stop = 0.2, speed?: number): void {
    const s = this.must(id);
    s.goal = speed === undefined ? { kind: "point", at: [...at], stop } : { kind: "point", at: [...at], stop, speed };
    if (!s.planned || len2(s.planned[0] - at[0], s.planned[2] - at[2]) > 0.5) s.replanIn = 0;
  }

  /** Move with a velocity (m/s), avoiding the others. */
  moveVelocity(id: number, v: NavVec3): void {
    this.must(id).goal = { kind: "velocity", v: [...v] };
  }

  /** Change an agent's body or limits (a boss's second phase runs faster). */
  updateSpec(id: number, spec: Partial<CrowdAgentSpec>): void {
    const s = this.must(id);
    s.spec = { ...s.spec, ...spec };
    this.live.get(id)!.updateParameters(this.params(s.spec));
  }

  /** Stop where it stands. */
  stop(id: number): void {
    this.must(id).goal = { kind: "none" };
  }

  /** Place an agent the game moves itself (the player): where it is and how fast it goes, for the others to avoid. */
  place(id: number, pos: NavVec3, vel: NavVec3): void {
    const s = this.must(id);
    s.pos = [...pos];
    s.vel = [...vel];
  }

  /** Move an agent instantly, at rest. */
  teleport(id: number, pos: NavVec3): void {
    const s = this.must(id);
    s.pos = [...pos];
    s.vel = [0, 0, 0];
    s.path = [];
    s.replanIn = 0;
  }

  /** An agent's state (read only: change it through the methods). */
  agent(id: number): Readonly<CrowdAgentState> | undefined {
    return this.states.get(id);
  }

  /** Every agent, by id. */
  agents(): readonly Readonly<CrowdAgentState>[] {
    return [...this.states.values()].sort((a, b) => a.id - b.id);
  }

  private desired(s: CrowdAgentState, dt: number): NavVec3 {
    if (s.external) return s.vel;
    const g = s.goal;
    if (g.kind === "none") {
      s.arrived = false;
      return [0, 0, 0];
    }
    if (g.kind === "velocity") {
      s.arrived = false;
      return g.v;
    }
    s.replanIn -= dt;
    if (s.replanIn <= 0 || s.path.length === 0) {
      const p = this.world.path(s.pos, g.at);
      s.path = p.corners;
      s.pathAt = Math.min(1, s.path.length - 1);
      s.planned = [...g.at];
      s.replanIn = this.options.replanEvery;
      this.replans++;
    }
    if (s.path.length === 0) return [0, 0, 0];
    const last = s.path[s.path.length - 1]!;
    const toEnd = len2(last[0] - s.pos[0], last[2] - s.pos[2]);
    if (toEnd <= g.stop) {
      s.arrived = true;
      return [0, 0, 0];
    }
    s.arrived = false;
    const reach = this.options.cornerRadius ?? 0.35;
    while (s.pathAt < s.path.length - 1 && len2(s.path[s.pathAt]![0] - s.pos[0], s.path[s.pathAt]![2] - s.pos[2]) < reach) s.pathAt++;
    const c = s.path[s.pathAt]!;
    const dx = c[0] - s.pos[0];
    const dz = c[2] - s.pos[2];
    const d = len2(dx, dz) || 1;
    // slow into the goal over a couple of radii
    const slow = Math.min(1, Math.max(0, (toEnd - g.stop) / (s.spec.radius * 2 + 0.5)));
    const speed = Math.min(g.speed ?? s.spec.maxSpeed, s.spec.maxSpeed) * Math.max(slow, 0.15);
    return [(dx / d) * speed, 0, (dz / d) * speed];
  }

  /** One fixed step of `dt` seconds: re-seat, steer, Detour's update, read back. */
  step(dt: number): void {
    const order = this.agents() as CrowdAgentState[];
    for (const s of order) {
      const a = this.live.get(s.id)!;
      if (!this.faults.noReseat || this.steps === 0 || s.external) {
        a.teleport({ x: s.pos[0], y: s.pos[1], z: s.pos[2] });
        const raw = a.raw;
        for (let k = 0; k < 3; k++) raw.set_vel(k, s.vel[k]!);
      }
      const v = this.desired(s, dt);
      a.requestMoveVelocity({ x: v[0], y: v[1], z: v[2] });
    }
    this.crowd.update(dt);
    for (const s of order) {
      if (s.external) continue;
      const a = this.live.get(s.id)!;
      if (a.state() === 0) continue; // off the mesh: Detour left it; keep ours
      const p = a.position();
      const v = a.velocity();
      s.pos = [p.x, p.y, p.z];
      s.vel = [v.x, v.y, v.z];
    }
    this.steps++;
  }

  /** The whole crowd as plain data. */
  snapshot(): CrowdSnapshot {
    return { format: "rpg.nav.crowd@1", agents: structuredClone(this.agents() as CrowdAgentState[]), steps: this.steps };
  }

  /** Rebuild the crowd from a snapshot: every agent back in its Detour slot with its state. */
  restore(snap: CrowdSnapshot): void {
    if (snap.format !== "rpg.nav.crowd@1") throw new Error(`nav crowd: not a crowd snapshot (${String((snap as { format?: unknown }).format)})`);
    this.crowd.destroy();
    this.crowd = this.fresh();
    this.live.clear();
    this.states.clear();
    const bySlot = [...snap.agents].sort((a, b) => a.slot - b.slot);
    const fillers: CrowdAgent[] = [];
    let next = 0;
    for (const s of bySlot) {
      // Detour hands out the lowest free slot: fill the gaps, then take the agent's own
      while (next < s.slot) {
        fillers.push(this.crowd.addAgent({ x: s.pos[0], y: s.pos[1], z: s.pos[2] }, this.params(s.spec)));
        next++;
      }
      const a = this.crowd.addAgent({ x: s.pos[0], y: s.pos[1], z: s.pos[2] }, this.params(s.spec));
      if (a.agentIndex !== s.slot) throw new Error(`nav crowd: agent ${s.id} restored into slot ${a.agentIndex}, not ${s.slot}`);
      next++;
      this.live.set(s.id, a);
      this.states.set(s.id, structuredClone(s));
    }
    for (const f of fillers) this.crowd.removeAgent(f);
    this.steps = snap.steps;
  }

  /** Counts for a report. */
  stats(): { agents: number; steps: number; replans: number } {
    return { agents: this.states.size, steps: this.steps, replans: this.replans };
  }

  dispose(): void {
    this.crowd.destroy();
    this.live.clear();
    this.states.clear();
  }
}
